import { styled } from "goober";
import { useCallback, useEffect } from "react";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { Icon } from "../components/Icon";
import { Text } from "../components/Text";
import { usePushLive } from "../hooks/usePushLive";
import { maybeDisplayBrowserNotification } from "../lib/browserNotifications";
import type { PushLiveBranch } from "../lib/executePushLive";

type Input = { summary?: unknown };
type Output = { pushed?: unknown };

function parseSummary(input: unknown): string | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const { summary } = input as Input;
  return typeof summary === "string" && summary.trim() ? summary : null;
}

function parseOutput(output: unknown): { pushed: boolean } | null {
  if (!output || typeof output !== "object" || Array.isArray(output))
    return null;
  const pushed = (output as Output).pushed;
  return typeof pushed === "boolean" ? { pushed } : null;
}

function PushLiveHeader({ result, interrupted }: ToolUseProps) {
  if (!result || result.isError) {
    return (
      <ToolStatus icon="rocket" text="Push live?" pending={!interrupted} />
    );
  }
  const output = parseOutput(result.output);
  if (!output?.pushed) {
    return <ToolStatus icon="circle-minus" text="Push live cancelled" />;
  }
  return (
    <ToolStatus icon="rocket" iconColor="var(--green-600)" text="Pushed live" />
  );
}

function PushLiveBody({ toolUse }: ToolUseProps) {
  const summary = parseSummary(toolUse.input);
  if (!summary) return null;
  return (
    <div style={{ padding: "2px 6px 8px 6px" }}>
      <Text variant="text-smaller-md" color="var(--text-tertiary)">
        {summary}
      </Text>
    </div>
  );
}

const PromptContainer = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  padding: 4px;
`;

const PromptBody = styled("div")`
  display: flex;
  flex-direction: column;
  padding-bottom: 4px;
  gap: 4px;
`;

const PromptHeader = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 8px;
  box-sizing: content-box;
  color: var(--text-secondary);
`;

const Row = styled("button")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  border: 0;
  background: transparent;
  text-align: left;
  width: 100%;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const ActionIcon = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const Keyboard = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 2px 4px;
  background: var(--alpha4);
  color: var(--text-secondary);
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  margin: 0 -4px -4px -4px;
  height: 40px;
  padding: 0 12px;
  border-top: 0.5px solid var(--border-alpha);
  justify-content: flex-end;
  @media (max-width: 768px) {
    display: none;
  }
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
`;

type OnResult = (
  output: unknown,
  isError: boolean,
  options?: { pauseAfter?: boolean }
) => void;

function PushLivePrompt({
  summary,
  branch,
  mainBranchId,
  spaceId,
  canPushLive,
  gitSynced,
  onResult,
}: {
  summary: string | null;
  branch: PushLiveBranch | undefined;
  mainBranchId: string | undefined;
  spaceId: string;
  canPushLive: boolean;
  gitSynced: boolean;
  onResult: OnResult;
}) {
  const { pushLive } = usePushLive({ branchId: branch?.id ?? "", spaceId });

  useEffect(() => {
    maybeDisplayBrowserNotification("3B needs you", "Push live?");
  }, []);

  const cancel = useCallback(
    () => onResult({ pushed: false }, false, { pauseAfter: true }),
    [onResult]
  );

  const confirm = useCallback(() => {
    onResult({ pushed: true }, false, { pauseAfter: true });
    pushLive(branch, mainBranchId);
  }, [onResult, pushLive, branch, mainBranchId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancel();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [cancel]);

  const blockedReason = gitSynced
    ? "This space syncs with a git repository. Sync the draft and merge it on the remote instead."
    : !canPushLive
      ? "You don’t have permission to push live in this space."
      : !branch || branch.name === "main"
        ? "There’s nothing to push — you’re on the live branch."
        : null;

  return (
    <PromptContainer>
      <PromptBody>
        <PromptHeader>
          <Text variant="text-small">Push this workflow live?</Text>
          <Text variant="text-smaller-md" color="var(--text-tertiary)">
            {summary ?? "Promotes your draft to the live main branch."}
          </Text>
        </PromptHeader>
        {blockedReason ? (
          <>
            <PromptHeader>
              <Text variant="text-smaller-md" color="var(--text-tertiary)">
                {blockedReason}
              </Text>
            </PromptHeader>
            <Row type="button" onClick={cancel}>
              <ActionIcon>
                <Icon
                  name="circle-minus"
                  size={16}
                  color="var(--text-primary)"
                />
              </ActionIcon>
              <Text variant="text-regular" color="var(--text-primary)">
                Dismiss
              </Text>
            </Row>
          </>
        ) : (
          <>
            <Row type="button" onClick={confirm}>
              <ActionIcon>
                <Icon name="rocket" size={16} color="var(--text-primary)" />
              </ActionIcon>
              <Text variant="text-regular" color="var(--text-primary)">
                Push live
              </Text>
            </Row>
            <Row type="button" onClick={cancel}>
              <ActionIcon>
                <Icon
                  name="circle-minus"
                  size={16}
                  color="var(--text-primary)"
                />
              </ActionIcon>
              <Text variant="text-regular" color="var(--text-primary)">
                Cancel
              </Text>
            </Row>
          </>
        )}
      </PromptBody>
      <Footer>
        <Instructions>
          <Text variant="text-smaller-md">Cancel</Text>
          <Keyboard>
            <Text variant="text-smaller-md">esc</Text>
          </Keyboard>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}

export const pushLive = (opts: {
  branch: PushLiveBranch | undefined;
  mainBranchId: string | undefined;
  spaceId: string;
  canPushLive: boolean;
  gitSynced: boolean;
}): ChatTool => ({
  name: "pushLive",
  Header: PushLiveHeader,
  Body: PushLiveBody,
  alwaysLoadOutput: true,
  renderPendingPrompt: ({ input, onResult }) => (
    <PushLivePrompt
      summary={parseSummary(input)}
      branch={opts.branch}
      mainBranchId={opts.mainBranchId}
      spaceId={opts.spaceId}
      canPushLive={opts.canPushLive}
      gitSynced={opts.gitSynced}
      onResult={onResult}
    />
  ),
});
